import { GetTokenSilentlyOptions, LogoutOptions } from '@auth0/auth0-react';
import { store } from '../redux/store';
import { isTokenExpired } from '../utils/isTokenExpired';
import refreshTokenOrLogout from '../utils/refreshTokenOrLogout';
import { PostHog } from 'posthog-js';
import fetchWithRetry from './fetchWithRetry';
import { addLearnosityBffBreadcrumb, captureLearnosityException } from '../utils/learnositySentry';

interface Params {
  activityId: string;
  userIds: { id: string; name: string }[];
  sessionIds: string[];
  userId: string;
  reportId: string;
  reportType: string;
}

export const getLearnosityReports = async (
  params: Params,
  getAccessTokenSilently: (options: GetTokenSilentlyOptions) => Promise<string>,
  logout: (options?: LogoutOptions) => Promise<void>,
  posthog: PostHog,
) => {
  let accessToken = store.getState().accessToken.value;

  const fields = {
    activityReference: params.activityId,
    learnerIds: params.userIds.map(user => user.id),
    sessionIds: params.sessionIds,
  };
  addLearnosityBffBreadcrumb('reports', fields);

  if (isTokenExpired()) {
    accessToken = (await refreshTokenOrLogout(getAccessTokenSilently, logout, posthog)) || null;
  }

  try {
    const result = await fetchWithRetry(`${import.meta.env.VITE_BFF_URL}/v1/learnosity/learnosity-report`, {
      method: 'POST',
      headers: {
        'Content-type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(params),
    });

    const data = await result.json();

    addLearnosityBffBreadcrumb('reports', { ...fields, status: result.status });

    return data;
  } catch (error) {
    captureLearnosityException(error, { operation: 'reports', ...fields });
    // Re-throw so callers can clear their loading state and show an error screen
    // instead of hanging forever on an `undefined` result.
    throw error;
  }
};
